<template>
    <div>
        <v-card>
            <v-card-title class="d-flex align-center">
                <span>Encuestas</span>
            </v-card-title>
            <v-card-text>
                <v-progress-linear v-if="loadingSurveys" indeterminate color="primary" class="mb-4" />
                <v-list v-else>
                    <v-list-item v-for="survey in surveys" :key="survey.id">
                        <v-list-item-title>Encuesta {{ survey.id }}</v-list-item-title>
                        <v-list-item-subtitle>
                            {{ getUserName(survey.userId) }}<template v-if="survey.date"> — {{ survey.date }}</template>
                        </v-list-item-subtitle>

                        <template #append>
                            <v-btn @click="editSurvey(survey)" icon="mdi-pencil" color="primary" variant="text" size="small" />
                            <v-btn @click="confirmDeleteSurvey(survey)" icon="mdi-delete" color="red" variant="text" size="small" />
                        </template>
                    </v-list-item>

                    <v-list-item v-if="surveys.length === 0">
                        <v-list-item-title class="text-center text-grey">No hay encuestas</v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-card-text>
        </v-card>

        <v-dialog v-model="showDeleteConfirmation" max-width="400">
            <v-card>
                <v-card-title>
                    <v-icon icon="mdi-alert-circle" color="red" />
                    <span class="ml-2">Eliminar encuesta</span>
                </v-card-title>
                <v-card-text>¿Está seguro de que desea eliminar la encuesta {{ toDeleteSurvey?.id }}?</v-card-text>
                <v-card-actions class="d-flex justify-end">
                    <v-btn @click="showDeleteConfirmation = false">Cancelar</v-btn>
                    <v-btn @click="removeSurvey" color="red" variant="tonal">Eliminar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script lang="ts" setup>
import { AuthService, SystemUser } from '@/services/auth/authService';
import { ResumedSurveyData } from '@/services/survey-response/models/surveyModels';
import { SurveyRespondeService } from '@/services/survey-response/surveyRespondeService';
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toast-notification';

const router = useRouter();
const surveyResponseService = new SurveyRespondeService();
const authService = new AuthService();
const surveys = ref<ResumedSurveyData[]>([]);
const users = ref<SystemUser[]>([]);
const toast = useToast();
const loadingSurveys = ref(true);

async function fetchSurveys() {
    loadingSurveys.value = true;
    try {
        surveys.value = (await surveyResponseService.getAllSurveys() || []).sort((a, b) => b.id - a.id);
    } catch (error) {
        console.error('Error fetching surveys:', error);
        toast.error('No se pudieron cargar las encuestas');
    } finally {
        loadingSurveys.value = false;
    }
}

async function fetchUsers() {
    try {
        const response = await authService.getAllUsers();
        if (response) users.value = response;
    } catch (error) {
        console.error('Error fetching users:', error);
    }
}

function getUserName(userId: number): string {
    const user = users.value.find(user => user.id === userId);
    return user ? `${user.name} (${user.username})` : 'Usuario desconocido';
}

function editSurvey(survey: ResumedSurveyData) {
    router.push({ name: 'Survey', params: { id: survey.id } });
}

const showDeleteConfirmation = ref(false);
const toDeleteSurvey = ref<ResumedSurveyData>();

async function removeSurvey() {
    if (!toDeleteSurvey.value) {
        toast.error('No se pudo eliminar la encuesta');
        return;
    }

    try {
        const response = await surveyResponseService.deleteSurvey(toDeleteSurvey.value.id);
        if (!response) throw new Error('No se pudo eliminar la encuesta');
        toast.success('Encuesta eliminada correctamente');
        await fetchSurveys();
    } catch (error) {
        console.error('Error removing survey:', error);
        toast.error('No se pudo eliminar la encuesta. Hable con soporte');
    } finally {
        showDeleteConfirmation.value = false;
        toDeleteSurvey.value = undefined;
    }
}

function confirmDeleteSurvey(survey: ResumedSurveyData) {
    toDeleteSurvey.value = survey;
    showDeleteConfirmation.value = true;
}

onMounted(() => {
    fetchSurveys();
    fetchUsers();
});
</script>
